import type { PayloadAction } from '@reduxjs/toolkit';
import { createSelector } from '@reduxjs/toolkit';
import type { UnitedCanvasReducerState } from './unitedCanvasReducer';
import type { RootState } from '..';
import { FunnelType } from '../../types/enums/FunnelType';
import type { CanvasGridPosition } from '../../components/sweep-canvas/helpers/gridPositioningUtils';
import type { FreePosition } from '../../types/FreePosition';

export const sectionsReducer = {
  addSectionToCanvas: (
    state: UnitedCanvasReducerState,
    action: PayloadAction<{
      sectionId: string;
      xyPosition: FreePosition;
      width: number;
      height: number;
      color: string;
      name: string;
      nodesToMove?: Array<{
        id: string;
        position: CanvasGridPosition;
        xyPosition: FreePosition;
        type: FunnelType;
      }>;
    }>,
  ) => {
    if (!state.funnelMap) {
      return;
    }

    const { sectionId, xyPosition, width, height, color, name, nodesToMove } = action.payload;

    // Initialize sections if they don't exist
    if (!state.funnelMap.sections) {
      state.funnelMap.sections = {};
    }

    state.funnelMap.sections[sectionId] = {
      sectionId,
      xyPosition: xyPosition,
      width,
      height,
      backgroundColor: color,
      name,
    };
    if (nodesToMove) {
      nodesToMove.forEach((node) => {
        switch (node.type) {
          case FunnelType.METADATA_ELEMENT:
            if (state.funnelMap.metadataElementsData[node.id]) {
              state.funnelMap.metadataElements[node.id] = {
                ...state.funnelMap.metadataElements[node.id],
                position: node.position,
                sectionId,
              };
            }
            break;
          case FunnelType.THIRD_PARTY:
            if (state.funnelMap.thirdPartyFunnelsData[node.id]) {
              state.funnelMap.thirdPartyFunnels[node.id] = {
                ...state.funnelMap.thirdPartyFunnels[node.id],
                position: node.position,
                sectionId,
              };
            }
            break;
          case FunnelType.SALESFORCE:
            if (state.funnelMap.funnels[node.id]) {
              state.funnelMap.funnels[node.id] = {
                ...state.funnelMap.funnels[node.id],
                position: node.position ?? { row: 0, column: 0 },
                sectionId,
              };
            }
            break;
          case FunnelType.STICKY_NOTE:
            if (state.funnelMap.stickyNotes?.[node.id]) {
              state.funnelMap.stickyNotes[node.id] = {
                ...state.funnelMap.stickyNotes[node.id],
                xyPosition: node.xyPosition,
                sectionId,
              };
            }
            break;
          default:
            break;
        }
      });
    }
  },

  moveSectionOnCanvas: (
    state: UnitedCanvasReducerState,
    action: PayloadAction<{
      sectionId: string;
      newPosition: FreePosition;
    }>,
  ) => {
    if (!state.funnelMap?.sections) {
      return;
    }

    const { sectionId, newPosition } = action.payload;
    const section = state.funnelMap.sections[sectionId];

    if (section) {
      section.xyPosition = newPosition;
    }
  },

  resizeSectionOnCanvas: (
    state: UnitedCanvasReducerState,
    action: PayloadAction<{
      sectionId: string;
      width: number;
      height: number;
      xyPosition: FreePosition;
    }>,
  ) => {
    if (!state.funnelMap?.sections) {
      return;
    }

    const { sectionId, width, height, xyPosition } = action.payload;
    const section = state.funnelMap.sections[sectionId];

    if (section) {
      section.width = width;
      section.height = height;
      section.xyPosition = xyPosition;
    }
  },

  updateSectionData: (
    state: UnitedCanvasReducerState,
    action: PayloadAction<{
      sectionId: string;
      color?: string;
      name?: string;
    }>,
  ) => {
    if (!state.funnelMap?.sections) {
      return;
    }

    const { sectionId, color, name } = action.payload;
    const section = state.funnelMap.sections[sectionId];

    if (section) {
      if (color !== undefined) {
        section.backgroundColor = color;
      }
      if (name !== undefined) {
        section.name = name;
      }
    }
  },

  removeSectionFromCanvas: (
    state: UnitedCanvasReducerState,
    action: PayloadAction<{
      sectionId: string;
    }>,
  ) => {
    if (!state.funnelMap?.sections) {
      return;
    }

    const { sectionId } = action.payload;
    delete state.funnelMap.sections[sectionId];
  },

  ungroupSectionFromCanvas: (
    state: UnitedCanvasReducerState,
    action: PayloadAction<{
      sectionId: string;
      nodesToMove: Array<{
        nodeId: string;
        newPosition: CanvasGridPosition;
        xyPosition: FreePosition;
        type: string;
      }>;
    }>,
  ) => {
    if (!state.funnelMap) {
      return;
    }

    const { sectionId, nodesToMove } = action.payload;

    // Update each child node to remove sectionId and update position
    nodesToMove.forEach((node) => {
      switch (node.type) {
        case FunnelType.METADATA_ELEMENT:
          if (state.funnelMap.metadataElements?.[node.nodeId]) {
            state.funnelMap.metadataElements[node.nodeId] = {
              ...state.funnelMap.metadataElements[node.nodeId],
              position: node.newPosition,
              sectionId: undefined,
            };
          }
          break;
        case FunnelType.THIRD_PARTY:
          if (state.funnelMap.thirdPartyFunnels?.[node.nodeId]) {
            state.funnelMap.thirdPartyFunnels[node.nodeId] = {
              ...state.funnelMap.thirdPartyFunnels[node.nodeId],
              position: node.newPosition,
              sectionId: undefined,
            };
          }
          break;
        case FunnelType.SALESFORCE:
          if (state.funnelMap.funnels?.[node.nodeId]) {
            state.funnelMap.funnels[node.nodeId] = {
              ...state.funnelMap.funnels[node.nodeId],
              position: node.newPosition,
              sectionId: undefined,
            };
          }
          break;
        case FunnelType.STICKY_NOTE:
          if (state.funnelMap.stickyNotes?.[node.nodeId]) {
            state.funnelMap.stickyNotes[node.nodeId] = {
              ...state.funnelMap.stickyNotes[node.nodeId],
              xyPosition: node.xyPosition,
              sectionId: undefined,
            };
          }
          break;
        default:
          break;
      }
    });

    // Remove the section itself
    if (state.funnelMap.sections) {
      delete state.funnelMap.sections[sectionId];
    }
  },

  removeSectionAndContents: (
    state: UnitedCanvasReducerState,
    action: PayloadAction<{
      sectionId: string;
    }>,
  ) => {
    const { sectionId } = action.payload;

    if (state.funnelMap?.metadataElements) {
      Object.values(state.funnelMap.metadataElements).forEach((metadataElement) => {
        if (metadataElement.sectionId === sectionId) {
          delete state.funnelMap.metadataElementsData[metadataElement.metadataId];
          delete state.funnelMap.metadataElements[metadataElement.metadataId];
        }
      });
    }
    if (state.funnelMap?.stickyNotes) {
      Object.values(state.funnelMap.stickyNotes).forEach((stickyNote) => {
        if (stickyNote.sectionId === sectionId) {
          delete state.funnelMap?.stickyNotes?.[stickyNote.stickyNoteId];
        }
      });
    }
    if (state.funnelMap?.funnels) {
      Object.entries(state.funnelMap.funnels).forEach(([funnelId, funnel]) => {
        if (funnel?.sectionId === sectionId) {
          delete state.funnelMap.funnels[funnelId];
          delete state.funnelMap.funnelsData[funnelId];
        }
      });
    }
    if (state.funnelMap?.thirdPartyFunnels) {
      Object.entries(state.funnelMap.thirdPartyFunnels).forEach(
        ([thirdPartyFunnelId, thirdPartyFunnel]) => {
          if (thirdPartyFunnel?.sectionId === sectionId) {
            delete state.funnelMap.thirdPartyFunnels[thirdPartyFunnelId];
            delete state.funnelMap.thirdPartyFunnelsData[thirdPartyFunnelId];
          }
        },
      );
    }
    if (state.funnelMap?.sections) {
      delete state.funnelMap.sections[sectionId];
    }
  },

  clearAllSections: (state: UnitedCanvasReducerState) => {
    if (state.funnelMap) {
      state.funnelMap.sections = {};
    }
  },
};

// Selectors
export const selectSections = (state: RootState) => state.unitedCanvas.present.funnelMap?.sections;

export const selectSectionById = (sectionId: string) => (state: RootState) =>
  state.unitedCanvas.present.funnelMap?.sections?.[sectionId];

export const selectSectionIds = createSelector([selectSections], (sections) =>
  Object.keys(sections ?? {}),
);

export const selectSectionsCount = createSelector(
  [selectSections],
  (sections) => Object.keys(sections ?? {}).length,
);
